import { If, P, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import Page from '../Page';
import ASNHeader from './ASNHeader';
import { useGetUserPurchaseOrder } from '../../Lib/Hooks/purchase-orders.hooks';
import {
  useGetAsnByPoNumber,
  useSaveAsnConfiguration,
} from '../../Lib/Hooks/ASN';
import { AsnResponse } from '../../Lib/Api/asn';
import { formatAsnLastSaved } from '../../Lib/Utils/Asn/getAsnLastEditedDate';
import ShipmentDetails from './ShipmentDetails';
import {
  asnSubmitSchema,
  AsnSubmitFormData,
} from './schemas/asnConfiguration.schema';
import { PageLoading } from '../PageLoading/PageLoading';

function mapAsnToFormDefaults(asn: AsnResponse): AsnSubmitFormData {
  return {
    header: {
      port_of_loading: asn.port_of_loading ?? null,
      port_of_entry: asn.port_of_entry ?? null,
      shipment_type: asn.shipment_type ?? null,
      trailer_number: asn.trailer_number ?? null,
      bol_number: asn.bol_number ?? null,
      carrier_name: asn.carrier_name ?? null,
      carrier_scac: asn.carrier_scac ?? null,
      seal_number: asn.seal_number ?? null,
      ship_date: asn.ship_date ?? null,
    },
    sku_configurations: (asn.sku_configurations ?? []).map((sku) => ({
      id: sku.id,
      actual_quantity: sku.actual_quantity ?? null,
      items_per_carton: sku.items_per_carton ?? null,
      carton_size: sku.carton_size ?? null,
      carton_weight: sku.carton_weight ?? null,
      carton_length: sku.carton_length ?? null,
      carton_width: sku.carton_width ?? null,
      carton_height: sku.carton_height ?? null,
      tare_weight: sku.tare_weight ?? null,
    })),
  };
}

function ASN() {
  const history = useHistory();
  const { purchaseOrderNumber, id: currentUserId } = useParams<{
    purchaseOrderNumber: string;
    id: string;
  }>();

  const poQuery = useGetUserPurchaseOrder(currentUserId, purchaseOrderNumber);
  const asnQuery = useGetAsnByPoNumber(currentUserId, purchaseOrderNumber);

  const saveAsnMutation = useSaveAsnConfiguration(
    currentUserId,
    asnQuery.data?.asn_number ?? '',
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState<DateTime | null>(null);

  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const methods = useForm<AsnSubmitFormData>({
    resolver: zodResolver(asnSubmitSchema),
    defaultValues: {
      header: {
        port_of_loading: null,
        port_of_entry: null,
        shipment_type: null,
        trailer_number: null,
        bol_number: null,
        carrier_name: null,
        carrier_scac: null,
        seal_number: null,
        ship_date: null,
      },
      sku_configurations: [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (asnQuery.data) {
      methods.reset(mapAsnToFormDefaults(asnQuery.data));
    }
  }, [asnQuery.data, methods]);

  useEffect(() => {
    const subscription = methods.watch((_value, { name }) => {
      if (!name) return;

      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        saveAsnMutation.mutate(methods.getValues(), {
          onSuccess: () => setLastSavedAt(DateTime.now()),
        });
      }, 1000);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [methods]);

  const handleContinue = () => {
    methods.handleSubmit((values) => {
      // TODO: Replace with POST /users/asn/:asnId/submit when backend is ready
      // eslint-disable-next-line no-console
      console.log('asn header submit', values);
      setCurrentStep((prev) => prev + 1);
    })();
  };

  const lastSaved = (() => {
    if (lastSavedAt) {
      const diffSeconds = DateTime.now().diff(lastSavedAt, 'seconds').seconds;
      if (diffSeconds < 5) return 'now';
      return lastSavedAt.toRelative() ?? lastSavedAt.toFormat('LLL dd, yyyy');
    }
    if (asnQuery.data) {
      return formatAsnLastSaved(asnQuery.data);
    }
    return '';
  })();
  const isLoading = poQuery.isLoading || asnQuery.isLoading;
  const isError = poQuery.isError || asnQuery.isError;

  return (
    <FormProvider {...methods}>
      <Page.Container className="bg-neutral-100 z-50 absolute left-0">
        <ASNHeader
          poNumber={purchaseOrderNumber}
          onContinue={handleContinue}
          onClose={() =>
            history.push(
              `/users/${currentUserId}/purchase-orders/${purchaseOrderNumber}`,
            )
          }
          currentStep={currentStep}
          lastSaved={lastSaved}
        />
        <Page.Content>
          <Stack direction="row" gap="sm" className="h-full">
            {!isLoading && !isError && asnQuery.data ? (
              <>
                <ShipmentDetails asnHeader={asnQuery.data} />
                <div
                  className="w-full h-full bg-neutral-0 content-center rounded-xl border border-neutral-200"
                  data-testid="asn-carton-label-details"
                >
                  Carton Label Details
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <If condition={isLoading}>
                  <PageLoading />
                </If>
                <If condition={isError && !isLoading}>
                  <P>
                    An error occurred while fetching the ASN. Please try again
                    later. If the problem persists, please contact support.
                  </P>
                </If>
              </div>
            )}
          </Stack>
        </Page.Content>
      </Page.Container>
    </FormProvider>
  );
}

export default ASN;
